import { initializeApp } from 'firebase/app';
import { getFirestore, collection, addDoc, onSnapshot, serverTimestamp, query, orderBy, limit, where } from 'firebase/firestore';
import firebaseConfig from './firebase-applet-config.json';

const app = initializeApp(firebaseConfig);
export const db = getFirestore(app, firebaseConfig.firestoreDatabaseId);

export enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

export interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId?: string | null;
    email?: string | null;
  };
}

export function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const errInfo: FirestoreErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: null,
      email: null,
    },
    operationType,
    path,
  };
  console.error('Firestore Error: ', JSON.stringify(errInfo));
  throw new Error(JSON.stringify(errInfo));
}

export interface UserReview {
  id: string;
  rating: number;
  name?: string;
  calculator?: string;
  createdAt: any;
}

/**
 * Submits a star rating to Firestore.
 */
export async function submitReview(rating: number, calculator: string, name?: string): Promise<void> {
  const collectionName = 'reviews';
  try {
    // Generate a secure random or custom ID or let Firestore generate it
    const reviewsRef = collection(db, collectionName);
    await addDoc(reviewsRef, {
      rating: Math.floor(rating),
      calculator,
      name: name || 'Anonym',
      createdAt: serverTimestamp(),
    });
  } catch (error) {
    handleFirestoreError(error, OperationType.WRITE, collectionName);
  }
}

/**
 * Subscribes to the latest reviews in real-time.
 */
export function subscribeToReviews(calculator: string, onUpdate: (reviews: UserReview[]) => void, onError: (error: any) => void) {
  const collectionName = 'reviews';
  const q = query(
    collection(db, collectionName),
    orderBy('createdAt', 'desc'),
    limit(300)
  );
  
  return onSnapshot(q, (snapshot) => {
    const list: UserReview[] = [];
    snapshot.forEach((doc) => {
      const data = doc.data();
      // In-memory filter on client side to avoid composite index requirements
      const calcId = data.calculator || 'brutto-netto';
      if (calcId === calculator) {
        list.push({
          id: doc.id,
          rating: data.rating || 0,
          name: data.name || 'Anonym',
          calculator: calcId,
          createdAt: data.createdAt,
        });
      }
    });
    onUpdate(list);
  }, (error) => {
    try {
      handleFirestoreError(error, OperationType.GET, collectionName);
    } catch (e) {
      onError(e);
    }
  });
}
